import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import {
  type ActionDialogAction,
  type ActionsDialog as ActionsDialogType,
} from "@sunrise/dialogs";
import { useTranslatable } from "@sunrise/translator";
import { socketAtom } from "@sunrise/yallo-websocket";
import clsx from "clsx";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { ReactNode, useCallback, useEffect } from "react";

import { FocusButton } from "@/components";
import type { DialogOptions } from "@/features/dialogs/types";
import { pageIdAtom } from "@/features/monitoring/page-id.atom";

import * as styles from "./actions-dialog.css";

/**
 * Contents of the dialog when type: 'actions' is set in the dialogsAtom.
 *
 * It will show a list of actions the user can take and focus either on the first one or
 * on the one that requires initial focus from the atom.
 */
export function ActionsDialog({
  dialog,
  "data-testid": testId,
  doClose,
}: DialogOptions & { dialog: ActionsDialogType }): ReactNode {
  const actionIndex = dialog.actions.findIndex((v) => v.initialFocus);
  const t = useTranslatable();

  const { focusKey, focusSelf, ref, focused, hasFocusedChild } = useFocusable({
    isFocusBoundary: true,
    focusable: !!dialog,
    preferredChildFocusKey:
      actionIndex >= 0
        ? getChildKeyForAction(dialog.actions[actionIndex], actionIndex)
        : undefined,
  });

  // WEBSOCKET message:
  const websocket = useAtomValue(socketAtom);
  const getPageId = useAtomCallback(
    useCallback((get) => {
      return get(pageIdAtom);
    }, []),
  );

  useEffect(() => {
    if (!dialog.technicalErrorName) return;

    const pageId = getPageId();
    if (websocket && pageId) {
      websocket.logError(dialog.technicalErrorName, pageId);
    }
  }, [dialog.id, dialog.technicalErrorName, getPageId, websocket]);

  // We need the dialog to be in there because we need to re-focus when the dialog contents change.
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!focused && !hasFocusedChild && dialog) {
      focusSelf();
    }
  }, [focusSelf, focused, hasFocusedChild, dialog]);

  const leftAlignment = dialog.textAlignment === "left";

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={clsx([
          styles.buttons,
          leftAlignment && styles.buttonsLeftAligned,
        ])}
        data-testid={`${testId}.actions`}
      >
        {"actions" in dialog &&
          dialog.actions.map((action, i) => {
            const key = getChildKeyForAction(action, i);

            return (
              <FocusButton
                block={!leftAlignment}
                focusKey={key}
                key={key}
                data-testid={`${testId}.button.${key}`}
                text={t(action.label) ?? key ?? `button ${i}`}
                onEnterPress={() => {
                  void action.action();
                  if (!action.closeDisabled) {
                    doClose();
                  }
                }}
                className={styles.button}
              />
            );
          })}
      </div>
    </FocusContext.Provider>
  );
}
function getChildKeyForAction(
  action: ActionDialogAction | undefined,
  index: number | undefined,
): string | undefined {
  if (!action || typeof index === "undefined" || index < 0) {
    return undefined;
  }

  return action.key ?? "action-" + index;
}
